import React from 'react';
import {useDispatch} from 'react-redux';
import {t} from 'i18next';
import {useMutation} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {setConnectionIdAction} from '../../../store/actions/connection.actions';
import {useFetchUsersForConnection} from '../auth/queries';
import {UserWithConnection} from '../../../incase-domain/user';
import {CONNECTION_USERS_PAGE_SIZE_DEFAULT_VALUES} from '../../../constants';
import {IUpdateError} from '../../../store/actions/types/error.types';
import {IConnectionRequest} from '../../../incase-domain/connection';
import {sendConnectionRequest} from '../../../service/connection.service';
import Button from '../../../incase-domain/components/button/Button';
import {useListParamsState} from '../../../hooks/useListParamsState';
import {createDataViewListStateProps} from '../../../hooks/listParams';
import DataView, {ViewMode} from '../../components/common/data/DataView';
import {PaginationPosition} from '../../components/common/data/pagination/Paginator';

interface ConnectionsModalAddConnectionProps {
    onNewConnectionRequestAdded: () => void;
}

function ConnectionsModalAddConnection(props: ConnectionsModalAddConnectionProps): JSX.Element {
    const {onNewConnectionRequestAdded} = props;

    const dispatch = useDispatch();
    const {listParams, setListParams} = useListParamsState({
        size: CONNECTION_USERS_PAGE_SIZE_DEFAULT_VALUES.pageSize,
    });
    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {data: userListForConnection, refetch: refetchUsers} = useFetchUsersForConnection({
        searchString: listParams.search,
        pageNumber: listParams.page,
        pageSize: listParams.size,
    });

    const sendConnectionRequestMutation = useMutation<IConnectionRequest, IUpdateError, string>(
        (recipientId) => {
            return sendConnectionRequest(recipientId);
        },
        {
            onSuccess: (data) => {
                dispatch(setConnectionIdAction(data._id));
                refetchUsers();
                onNewConnectionRequestAdded();
            },
            onError: (error) => {
                toast.error(t(error.error.message));
            },
        }
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderUserImage = (userWithConnection: UserWithConnection): JSX.Element => {
        return <Button leading="solar:user-linear" variant="soft" size="sm" rounded square disablePointerEvents />;
    };

    const renderUsernameEmail = (userWithConnection: UserWithConnection): JSX.Element => {
        return (
            <>
                <span>
                    <strong>{userWithConnection.user.username}</strong>
                </span>
                <span>
                    <p>{userWithConnection.user.email}</p>
                </span>
            </>
        );
    };

    const renderRole = (userWithConnection: UserWithConnection): JSX.Element => {
        return userWithConnection.user.roles[0] ? (
            <Button variant="ghost" size="sm" disablePointerEvents>
                {userWithConnection.user.roles[0]}
            </Button>
        ) : (
            <></>
        );
    };

    const renderActions = (userWithConnection: UserWithConnection): JSX.Element => {
        return (
            <Button
                variant="soft"
                color={userWithConnection.isConnection ? 'primary' : 'secondary'}
                size="sm"
                square
                leading="solar:plain-3-outline"
                onClick={() => sendConnectionRequestMutation.mutate(userWithConnection.user._id)}
                disabled={userWithConnection.isConnection}
            >
                {t('connections.sendRequest')}
            </Button>
        );
    };

    return (
        <DataView
            views={[
                {
                    columns: [
                        {
                            body: (row) => renderUserImage(row),
                            width: '3rem',
                        },
                        {
                            label: t('connections.user'),
                            body: (row) => renderUsernameEmail(row),
                        },
                        {
                            label: t('connections.role'),
                            body: (row) => renderRole(row),
                            width: '10rem',
                        },
                        {
                            body: (row) => renderActions(row),
                        },
                    ],
                    view: ViewMode.TABLE,
                    hideHeader: true,
                },
            ]}
            rows={userListForConnection?.content || []}
            rowIdentifier={(row) => row.user._id}
            paginationData={{
                pageSize: listParams.size,
                currentPage: listParams.page,
                itemsCount: userListForConnection?.numberOfElements || 0,
            }}
            {...dataViewListState}
            enableSearch
            paginationPosition={PaginationPosition.BOTTOM}
        />
    );
}

export default ConnectionsModalAddConnection;
